import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Loading } from "@/components/Layout";
import { Info, Database, Television, FilmSlate, Users, Broadcast, Stack, CheckCircle, WarningCircle } from "@phosphor-icons/react";

export default function SystemInfo() {
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/system/info").then((r) => setInfo(r.data)); }, []);

  if (!info) return (<><PageHeader title="System Information" subtitle="Platform status & version" /><Loading /></>);

  const totals = [
    { key: "channels", label: "Channels", icon: Television },
    { key: "assets", label: "Assets", icon: FilmSlate },
    { key: "users", label: "Users", icon: Users },
    { key: "outputs", label: "Outputs", icon: Broadcast },
    { key: "blocks", label: "Blocks", icon: Stack },
  ];
  const dbConnected = info.db_status === "connected";
  const operational = info.platform_status === "operational";

  return (
    <>
      <PageHeader title="System Information" subtitle="Platform status & version" />
      <div className="p-8 max-w-4xl space-y-6 ccs-enter" data-testid="system-info-page">
        <div className="bg-[#141414] border border-[#262626] rounded-sm p-6">
          <h3 className="font-head font-semibold text-sm mb-5 flex items-center gap-2"><Info size={16} className="text-[#3A86FF]" /> Platform</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
            <Meta label="App Version" value={info.app_version} testid="sys-app-version" />
            <Meta label="Release" value={info.release} testid="sys-release" />
            <Meta label="Build Date" value={info.build_date} testid="sys-build-date" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Database</div>
              <div data-testid="sys-db-status" className={`flex items-center gap-1.5 text-sm font-mono ${dbConnected ? "text-emerald-500" : "text-red-500"}`}>
                <Database size={14} /> {info.db_status}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Platform Status</div>
              <div data-testid="sys-platform-status" className={`flex items-center gap-1.5 text-sm font-mono ${operational ? "text-emerald-500" : "text-amber-500"}`}>
                {operational ? <CheckCircle size={14} weight="fill" /> : <WarningCircle size={14} weight="fill" />} {info.platform_status}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-sm p-6">
          <h3 className="font-head font-semibold text-sm mb-5">Resource Totals</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {totals.map((t) => (
              <div key={t.key} data-testid={`sys-total-${t.key}`} className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-4">
                <t.icon size={18} className="text-[#3A86FF] mb-2" />
                <div className="font-head font-black text-2xl">{info.totals[t.key]}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">{t.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function Meta({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">{label}</div>
      <div data-testid={testid} className="text-sm font-mono text-white">{value}</div>
    </div>
  );
}
